import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import useQuery from 'hooks/useQuery'
import { filterEmptyValues } from 'utils/helpers'
import { Button, Dropdown, TextInput } from 'components/common'
import {
  AdminAuditLogQueryParams,
  AdminAuditModule,
  AdminAuditAction,
} from 'types/adminAuditLog'
import { DateTime } from 'components/common/input/dateTime'

type Props = {
  toggleFilterForm: () => void
}

/** Query-param key used to persist filters in the URL */
export const FILTER_FIELD_ADMIN_AUDIT_LOG = 'adminAuditLogFilters'

const AdminAuditLogFilterForm = ({ toggleFilterForm }: Props) => {
  const { getQuery, removeQuery, setMultipleQueries } = useQuery()
  const filters = getQuery(FILTER_FIELD_ADMIN_AUDIT_LOG) as
    | Partial<AdminAuditLogQueryParams>
    | undefined

  const [resetFilter, setResetFilter] = useState(false)

  const { handleSubmit, setValue, reset, register } = useForm<
    Partial<AdminAuditLogQueryParams>
  >({
    defaultValues: filters ?? {},
  })

  const onFilterApply = (data: Partial<AdminAuditLogQueryParams>) => {
    // Strip sentinel "NONE" values coming from Dropdown
    const cleaned = { ...data } as Record<string, unknown>
    if (cleaned.module === 'NONE') delete cleaned.module
    if (cleaned.action === 'NONE') delete cleaned.action

    const filteredData = filterEmptyValues(cleaned)

    if (Object.values(filteredData).length > 0) {
      setMultipleQueries({
        [FILTER_FIELD_ADMIN_AUDIT_LOG]: filteredData,
        page: 1,
      })
    } else {
      removeQuery(FILTER_FIELD_ADMIN_AUDIT_LOG)
    }

    toggleFilterForm()
  }

  const cancelFilter = () => {
    reset(filters ?? {})
    setResetFilter((prev) => !prev)
    toggleFilterForm()
  }

  // ── Dropdown options ──────────────────────────────────────────────────────

  const moduleOptions = useMemo(
    () => [
      { label: '--- NONE ---', value: 'NONE' },
      { label: 'Catalogue', value: AdminAuditModule.CATALOGUE },
      { label: 'Inventory', value: AdminAuditModule.INVENTORY },
      { label: 'Pricing', value: AdminAuditModule.PRICING },
      { label: 'Order', value: AdminAuditModule.ORDER },
      { label: 'Function Job', value: AdminAuditModule.FUNCTION_JOB },
      { label: 'Configuration', value: AdminAuditModule.CONFIGURATION },
      { label: 'User', value: AdminAuditModule.USER },
      { label: 'Store', value: AdminAuditModule.STORE },
      { label: 'Aggregator', value: AdminAuditModule.AGGREGATOR },
      { label: 'FTP Configuration', value: AdminAuditModule.FTP_CONFIGURATION },
      { label: 'Area', value: AdminAuditModule.AREA },
      { label: 'Picker Mapping', value: AdminAuditModule.PICKER_MAPPING },
      { label: 'FOC Order', value: AdminAuditModule.FOC_ORDER },
      { label: 'Chain', value: AdminAuditModule.CHAIN },
    ],
    [],
  )

  const actionOptions = useMemo(
    () => [
      { label: '--- NONE ---', value: 'NONE' },
      { label: 'Create', value: AdminAuditAction.CREATE },
      { label: 'Update', value: AdminAuditAction.UPDATE },
      { label: 'Delete', value: AdminAuditAction.DELETE },
      { label: 'Sync Price', value: AdminAuditAction.SYNC_PRICE },
      { label: 'Change Barcode', value: AdminAuditAction.CHANGE_BARCODE },
      { label: 'Change Product', value: AdminAuditAction.CHANGE_PRODUCT },
      { label: 'Change Store', value: AdminAuditAction.CHANGE_STORE },
      { label: 'Call Inventory', value: AdminAuditAction.CALL_INVENTORY },
      { label: 'Call Pricing', value: AdminAuditAction.CALL_PRICING },
      { label: 'Run Job', value: AdminAuditAction.RUN_JOB },
    ],
    [],
  )

  return (
    <div className="mt-2 rounded-lg border bg-white px-5 py-3">
      <div className="mx-auto max-w-5xl">
        <form onSubmit={handleSubmit(onFilterApply)}>
          <div className="grid grid-cols-2 gap-x-14 gap-y-3 md:grid-cols-3">
            {/* ── Enum filters ─────────────────────────────────── */}

            <Dropdown
              name="module"
              label="Module"
              setValue={setValue}
              defaultValue={filters?.module ?? resetFilter}
              options={moduleOptions}
            />

            <Dropdown
              name="action"
              label="Action"
              setValue={setValue}
              defaultValue={filters?.action ?? resetFilter}
              options={actionOptions}
            />

            {/* ── Entity filters ───────────────────────────────── */}

            <TextInput name="entityId" label="Entity ID" register={register} />

            {/* ── User filters ─────────────────────────────────── */}

            <TextInput name="userId" label="User ID" register={register} />

            <TextInput
              name="employeeId"
              label="Employee ID"
              register={register}
            />

            <TextInput name="userName" label="User Name" register={register} />

            {/* ── Date range ───────────────────────────────────── */}

            <DateTime
              name="startDate"
              label="Start Date"
              type="date"
              register={register}
            />

            <DateTime
              name="endDate"
              label="End Date"
              type="date"
              register={register}
            />
          </div>

          <div className="my-4 flex gap-5">
            <Button buttonText="Apply Filters" className="w-32" />
            <Button
              buttonText="Cancel"
              variant="outline"
              type="button"
              onClick={cancelFilter}
              className="w-32"
            />
          </div>
        </form>
      </div>
    </div>
  )
}

export default AdminAuditLogFilterForm
